(() => {
  const SELECTOR = '[data-privado-arcade]';
  const ACTIVE_CLASS = 'is-arcade-open';
  const BODY_CLASS = 'privado-arcade-at-top';
  const LOBBY_CLASS = 'privado-lobby-open';

  class PrivadoArcade {
    constructor(section) {
      this.section = section;
      this.slider = section.querySelector('slideshow-element');
      this.popups = Array.from(section.querySelectorAll('[data-arcade-index]'));
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      // Mobile keeps the always-on arcade hero instead of the pull-up takeover —
      // on phones that state just dims the hero uselessly (no separate arcade).
      this.small = window.matchMedia('(max-width: 749px)');
      this.queued = false;
      this.isOpen = false;
      this.touchStartY = null;
      this.touchStartedAtTop = false;

      this.onScroll = this.onScroll.bind(this);
      this.onWheel = this.onWheel.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
      this.onSliderChange = this.onSliderChange.bind(this);
      this.onSelectedIndexMutation = this.onSelectedIndexMutation.bind(this);

      this.bind();
      // Desktop: the arcade hero is permanent + scroll-through (managed by
      // update()). Mobile keeps its own always-on hero, so no takeover there.
      if (this.small.matches) {
        this.setOpen(false);
      } else {
        this.update();
        // Flickity may size the slideshow to the shorter peek height before
        // is-arcade-open lands; nudge it to recompute to the full arcade height
        // (a few times to catch its async init) so no gap shows below the hero.
        [150, 500, 1100, 2000].forEach((t) => setTimeout(() => window.dispatchEvent(new Event('resize')), t));
      }
    }

    bind() {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      window.addEventListener('wheel', this.onWheel, { passive: true });
      window.addEventListener('touchstart', this.onTouchStart, { passive: true });
      window.addEventListener('touchmove', this.onTouchMove, { passive: true });
      window.addEventListener('keydown', this.onKeydown);
      window.addEventListener('pageshow', this.onPageShow);
      this.slider?.addEventListener('slider:change', this.onSliderChange);

      if (this.slider) {
        this.observer = new MutationObserver(this.onSelectedIndexMutation);
        this.observer.observe(this.slider, { attributes: true, attributeFilter: ['selected-index'] });
        this.setActive(this.slider.selectedIndex || 0);
      }

      this.popups.forEach((popup) => {
        popup.addEventListener('pointerenter', () => this.selectFromPopup(popup));
        popup.addEventListener('focus', () => this.selectFromPopup(popup));
      });
    }

    getScrollY() {
      return window.scrollY || window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    }

    getCloseThreshold() {
      return Math.min(120, Math.max(48, window.innerHeight * 0.08));
    }

    isLobbyOpen() {
      return document.documentElement.classList.contains(LOBBY_CLASS) || document.body.classList.contains(LOBBY_CLASS);
    }

    isAtTopBaseline() {
      const rect = this.section.getBoundingClientRect();
      return this.getScrollY() <= 2 && rect.top <= window.innerHeight * 0.35;
    }

    onScroll() {
      if (this.queued) return;

      this.queued = true;
      requestAnimationFrame(() => {
        this.queued = false;
        this.update();
      });
    }

    onWheel(event) {
      if (event.deltaY < -8) {
        this.openFromTopIntent();
      }
    }

    onTouchStart(event) {
      const touch = event.touches && event.touches[0];
      this.touchStartY = touch ? touch.clientY : null;
      this.touchStartedAtTop = this.isAtTopBaseline();
    }

    onTouchMove(event) {
      const touch = event.touches && event.touches[0];
      if (!touch || this.touchStartY === null || !this.touchStartedAtTop) return;

      if (touch.clientY - this.touchStartY > 28) {
        this.openFromTopIntent();
      }
    }

    onKeydown(event) {
      if (!['ArrowUp', 'PageUp'].includes(event.key)) return;
      this.openFromTopIntent();
    }

    onPageShow() {
      this.touchStartY = null;
      this.touchStartedAtTop = false;
      this.setOpen(false);
      this.update();
    }

    onSliderChange(event) {
      this.setActive(event.detail.currentPage || 0);
    }

    onSelectedIndexMutation() {
      this.setActive(this.slider?.selectedIndex || 0);
    }

    selectFromPopup(popup) {
      const index = Number.parseInt(popup.dataset.arcadeIndex, 10);
      this.setActive(index);

      if (this.prefersReducedMotion.matches || !this.slider?.slider) return;
      this.slider.select(index);
    }

    setActive(index) {
      if (!this.popups.length) return;

      const activeIndex = index % this.popups.length;
      this.popups.forEach((popup) => {
        popup.classList.toggle('is-active', Number.parseInt(popup.dataset.arcadeIndex, 10) === activeIndex);
      });
    }

    openFromTopIntent() {
      if (this.small.matches || this.isLobbyOpen() || !this.isAtTopBaseline()) return;

      this.setOpen(true);
    }

    setOpen(isOpen) {
      if (isOpen === this.isOpen) return;

      this.isOpen = isOpen;
      this.section.classList.toggle(ACTIVE_CLASS, isOpen);
      document.body.classList.toggle(BODY_CLASS, isOpen);
    }

    update() {
      if (this.isLobbyOpen()) {
        this.setOpen(false);
        return;
      }

      // Desktop: the arcade is a PERMANENT, scroll-through fullscreen hero. Keep
      // the section open at all times (no collapse / layout shift), so you just
      // scroll through it like any hero. Only toggle the "at top" chrome (hidden
      // announcement bar + transparent header) based on whether the hero still
      // covers the top of the viewport — once scrolled past it, the normal header
      // returns so it stays readable over the content below.
      if (!this.small.matches) {
        this.section.classList.add(ACTIVE_CLASS);
        this.isOpen = true;
        const inHero = this.section.getBoundingClientRect().bottom > 120;
        document.body.classList.toggle(BODY_CLASS, inHero);
        return;
      }

      if (this.isOpen && this.getScrollY() > this.getCloseThreshold()) {
        this.setOpen(false);
      }
    }

    destroy() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      window.removeEventListener('wheel', this.onWheel);
      window.removeEventListener('touchstart', this.onTouchStart);
      window.removeEventListener('touchmove', this.onTouchMove);
      window.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('pageshow', this.onPageShow);
      this.slider?.removeEventListener('slider:change', this.onSliderChange);
      this.observer?.disconnect();
      this.setOpen(false);
    }
  }

  const instances = new WeakMap();

  const init = (root = document) => {
    root.querySelectorAll(SELECTOR).forEach((section) => {
      if (instances.has(section)) return;
      instances.set(section, new PrivadoArcade(section));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  }
  else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
